import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Select, Store} from '@ngxs/store';
import {Observable} from 'rxjs/internal/Observable';
import {ReportsState} from '../../../shared/states/reports/reports.state';
import {
  RunCustomReport,
  SaveCustomReportFormState,
  SetCustomReportRecords,
  SetLoading,
} from '../../../shared/states/reports/reports.actions';
import {SearchInputColorEnum} from '../../../shared/components/search-input/search-input.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BaseTableDirective} from '../../../shared/directives/base-table.directive';
import {ActivityTypesEnum} from '../../../shared/interfaces/ActivityTypesEnum';
import {BadgeColorsEnum} from '../../../shared/interfaces/BadgeColorsEnum';
import {getFlatObject, groupByProperty, sortByProperty} from '../../../shared/helpers/helpers';
import {DrugsState} from '../../../shared/states/drugs/drugs.state';
import {IDrug} from '../../../shared/interfaces/drug.interface';
import {GetDrugsForDrugsFilter} from '../../../shared/states/drugs/drugs.actions';
import {PERIODS_LIST} from '../../../shared/constants/search.constants';
import {ProfileState} from '../../../shared/states/profile/profile.state';
import {ITeamRecord} from '../../../team/interfaces/team-record.interface';
import {GetUserTeamList} from '../../../shared/states/profile/profile.actions';
import {UserStatusEnum} from '../../../shared/interfaces/UserStatusEnum';
import {AuthState} from '../../../shared/states/auth/auth.state';
import {DatePipe} from '@angular/common';
import {distinctUntilChanged, startWith, take} from 'rxjs/operators';
import {ReportsService} from '../../reports.service';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { SearchDateRange } from 'src/app/shared/interfaces/SearchDateRange';
import { Router } from '@angular/router';
import { ITransactionModel } from 'src/app/shared/interfaces/ITransactionModel';
import { SideWidgetService } from 'src/app/shared/services/side-widget.service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { DrugsService } from 'src/app/shared/services/drugs.service';
import { ActivityMultiSelectListContent } from 'src/app/shared/interfaces/activity-multi-select-list-content';
import { OrdersService } from 'src/app/inventory/components/receive-edi/orders.service';

@UntilDestroy()
@Component({
  selector: 'app-custom-reports',
  templateUrl: './custom-reports.component.html',
  styleUrls: ['./custom-reports.component.scss'],
})
export class CustomReportsComponent extends BaseTableDirective implements OnInit {
  customReportControlsForm: UntypedFormGroup;
  reportsTableControlsForm: UntypedFormGroup;

  tableColumns = {
    transactionDate: true,
    drugName: true,
    onHand: true,
    ['lastActivity.createDate']: true,
  };

  periodsList = [];

  originalRecordsList: ITransactionModel[] = [];
  sortedRecordsList: any[] = [];
  groupItemsOpened: boolean[] = [];

  isReportRunApplied: boolean;

  userItemsShowLimit = 2;
  drugItemsShowLimit = 2;
  activitiesItemsShowLimit = 2;

  isUsersDropdownOpened: boolean;
  isDrugsDropdownOpened: boolean;
  isActivityDropdownOpened: boolean;
  isFileDownloading: boolean;

  usersList = [];
  drugsList = [];

  originalTitle = 'Custom Report';
  title = this.originalTitle;

  activitiesList = ActivityMultiSelectListContent;

  userListSettings = {
    selectAllText: 'All Users',
    unSelectAllText: 'Deselect All',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Users',
    noDataAvailablePlaceholderText: 'No users available',
  };

  drugListSettings = {
    selectAllText: 'All Drugs',
    unSelectAllText: 'Deselect All',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Drugs',
    noDataAvailablePlaceholderText: 'No drugs available',
  };

  activitiesListSettings = {
    selectAllText: 'All Activity',
    unSelectAllText: 'Deselect All',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Activities',
    noDataAvailablePlaceholderText: 'No activities available',
  };

  filterResultsBy: 'user' | 'drug' = 'user';

  SearchInputColorEnum = SearchInputColorEnum;
  ActivityTypesEnum = ActivityTypesEnum;
  BadgeColorsEnum = BadgeColorsEnum;

  @Select(ReportsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(ReportsState.reportRecords) reportRecords$: Observable<ITransactionModel[]>;
  @Select(ReportsState.runningReports) runningReports$: Observable<boolean>;
  @Select(ReportsState.customReportForm) customReportForm$: Observable<any>;
  @Select(DrugsState.drugsFilterDrugItems) drugsFilterDrugItems$: Observable<IDrug[]>;
  @Select(ProfileState.teamList) teamList$: Observable<ITeamRecord[]>;
  @Select(InventoryState.loadingLibertyRxInventory) loadingLibertyRxInventory$: Observable<boolean>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  @ViewChild('periodDropdown') periodDropdown;
  @ViewChild('customPeriodDatepicker') customPeriodDatepicker;
  @ViewChild('usersMultioptionalDropdown') usersMultioptionalDropdown;
  @ViewChild('drugsMultioptionalDropdown') drugsMultioptionalDropdown;
  @ViewChild('activityMultioptionalDropdown') activityMultioptionalDropdown;
  @ViewChild('downloadMenu') downloadMenu;
  @ViewChild('tbody') tbody;


  private byUsers: boolean;
  private byDrugs: boolean;
  private byActivities: boolean;

  private schedulesInReport: number[];

  constructor(
    protected store: Store,
    private fb: UntypedFormBuilder,
    private datePipe: DatePipe,
    private reportsService: ReportsService,
    private router: Router,
    private sideWidgetService: SideWidgetService,
    private drugService: DrugsService,
    private ordersService: OrdersService,
  ) {
    super(store);

    this.customReportControlsForm = this.fb.group({
      period: ['', []],
      customPeriod: [null, []],
      users: [[], []],
      drugs: [[], []],
      activityTypes: [[], []],
      scrollTop: 0,
      schedules: null,
      inventoryLocations: null
    });

    this.reportsTableControlsForm = this.fb.group({
      searchValue: ['', []],
    });

    this.periodsList = [
      {id: SearchDateRange.Custom, name: 'Custom Range', value: undefined},
      ...PERIODS_LIST,
    ];

    if (!this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.activitiesList = this.activitiesList.filter(x => x.id !== ActivityTypesEnum.MovedFrom && x.id !== ActivityTypesEnum.MovedTo);
    }
  }

  ngOnInit(): void {
    this.reportRecords$.pipe(untilDestroyed(this))
      .subscribe((records: ITransactionModel[]) => {
        if (records && records.length) {
          this.originalRecordsList = records;
          this.filterBySearchValue(this.reportsTableControlsForm.get('searchValue').value);
        } else {
          this.originalRecordsList = [];
        }
        this.schedulesInReport = this.drugService.getSchedulesBasedOnExistingInventories(this.originalRecordsList as any)
        this.changeTitle(this.schedulesInReport || []);
      });

    this.store.dispatch(new GetDrugsForDrugsFilter());
    this.store.dispatch(new GetUserTeamList(UserStatusEnum.Active.toString()));

    this.teamList$.pipe(untilDestroyed(this))
      .subscribe((data: ITeamRecord[]) => {
        this.usersList = data?.map(el => ({id: el.id, name: el.userName})) || [];
      });

    this.drugsFilterDrugItems$.pipe(untilDestroyed(this))
      .subscribe((data: IDrug[]) => {
        this.populateDrugFilter(data, this.customReportControlsForm.value?.schedules?.map(x => x.id));
      });

    //restore filters
    this.customReportForm$.pipe(take(1))
      .subscribe(data => {
        this.customReportControlsForm.patchValue(data);
        setTimeout(() => {
          this.tbody?.nativeElement?.scrollTo(0, data?.scrollTop);
        });

        if (data?.scrollTop > 0) {
          setTimeout(() => {
            this.saveScrollPosition(0);
          }, 100);
        }
      });

    this.reportsTableControlsForm.get('searchValue')?.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(''),
        distinctUntilChanged(),
      )
      .subscribe((search: string) => {
        this.filterBySearchValue(search);
      });

    this.customReportControlsForm.get('schedules')?.valueChanges
    .pipe(
      untilDestroyed(this),
    )
    .subscribe((schedules: any) => {
      this.populateDrugFilter(this.store.selectSnapshot(DrugsState)?.drugsFilterDrugItems, schedules?.map(x => x.id));
    });

    this.enableC3toC5Drugs$.subscribe(enabled => {
      if (!enabled) {
        this.customReportControlsForm.get('schedules').reset();
      }
    });
  }

  changeTitle(existingSchedules: number[]): void {
    this.title = this.reportsService.composeReportTitleWithScheduleExtension(this.originalTitle, [...existingSchedules || []]);
  }

  focusMultioptionalDropdown(dropdownName: string): void {
    setTimeout(() => {
      this[dropdownName].opened = true;
      this[dropdownName].isFocused = true;
      this[dropdownName].dropDownOpened.emit();
      this[dropdownName].multiselectDropdown.nativeElement.focus();
    });
  }

  updateSortedList(source: ITransactionModel[]): void {
    const flatList = [...source].map((transaction: ITransactionModel) => {
      const flatTransaction = getFlatObject({...transaction, history: null});
      switch (this.filterResultsBy) {
        case 'user':
          flatTransaction.user = `${flatTransaction.firstName} ${flatTransaction.lastName}`;
          break;
        case 'drug':
          flatTransaction.drug = `${flatTransaction['friendlyName']} (${flatTransaction['packageNdc']})`;
          break;
      }

      return flatTransaction;
    });
    setTimeout(() => {
      this.sortedRecordsList = groupByProperty(flatList, this.filterResultsBy);
      this.groupItemsOpened = Array(this.sortedRecordsList.length).fill(true);
      this.store.dispatch(new SetLoading(false));
    });
  }

  reset(): void {
    this.customReportControlsForm.reset();
    this.resetCustomRange();
    this.reportsTableControlsForm.get('searchValue').setValue('');
    this.customReportControlsForm.get('schedules').setValue([]);
    this.store.dispatch(new SaveCustomReportFormState(null));
    this.originalRecordsList = [];
    this.store.dispatch(new SetCustomReportRecords(this.originalRecordsList));
  }

  runReport(): void {
    const raw = this.customReportControlsForm.getRawValue();
    const customDateRange = (this.periodsList.find(x => x.id === SearchDateRange.Custom) as any)?.value;
    
    this.store.dispatch(new SaveCustomReportFormState(this.customReportControlsForm.getRawValue()));
    this.store.dispatch(new RunCustomReport({
      startDate: customDateRange?.startDate,
      endDate: customDateRange?.endDate,
      userIds: raw?.users?.map(x => x.id),
      drugIds: raw?.drugs?.map(x => x.id) || [],
      activities: raw?.activityTypes?.map(x => x.id),
      pharmacyId: this.store.selectSnapshot(AuthState.pharmacyId),
      searchDateRange: !raw?.period ? 0 : raw.period,
      offset: new Date().getTimezoneOffset() / 60,
      schedules: raw?.schedules?.map(x => x.id),
      inventoryLocations: raw?.inventoryLocations?.map(x => x.id)
    }));

    this.byActivities = raw?.activityTypes?.length;
    this.byDrugs = raw?.drugs?.length;
    this.byUsers = raw?.users?.length;

    this.isReportRunApplied = true;
  }

  saveCSV(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    this.reportsService.downloadCustomReport({fileDtos: this.getEntitiesToExportCsv()})
      .pipe(take(1))
      .subscribe(res => {
        const name = this.title + ' from ' + new Date().toDateString() + '.csv';
        this.handleDownload(res, name);
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }
  
  savePDF(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    const raw = this.customReportControlsForm.getRawValue();
    const customDateRange = (this.periodsList.find(x => x.id === SearchDateRange.Custom) as any)?.value;
    this.reportsService.downloadCustomPDFReport({
      fileDtos: this.getEntitiesToExport(),
      startDate: customDateRange?.startDate?.toDateString(),
      endDate: customDateRange?.endDate?.toDateString(),
      searchDateRange: !raw?.period ? 0 : raw.period,
      byUsers: this.byUsers,
      byDrugs: this.byDrugs,
      byActivities: this.byActivities,
      isAllSchedulesMode: this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs),
      schedules: this.schedulesInReport
    })
    .pipe(take(1))
    .subscribe(res => {
      const name = this.title + ' from ' + new Date().toDateString() + '.pdf';
      this.handleDownload(res, name);
      this.store.dispatch(new SetLoading(false));
      this.isFileDownloading = false;
    }, err => {
      this.store.dispatch(new SetLoading(false));
      this.isFileDownloading = false;
    });
  }


  getEntitiesToExport() {
    const groups =  this.sortedRecordsList.map(group => group.transactions.map(x => {
        const localTime = new Date(x.transactionDate).toString();
        return {
        date: localTime.split("GMT")[0].trim(),
        User: x.lastName + ", " + x.firstName,
        drug: x['friendlyName'],
        ndc: x['packageNdc'],
        activity: x.activityType,
        location: x?.locationName,
        starting: x.startingQuantity,
        count: x.addedQuantity,
        onHand: x.endingQuantity,
        info1: x.infoHeading,
        info2: x.infoSecondary,
        info3: x?.infoSecondary2
        }})
      );

    const merged = [];
    groups.forEach(array => {
      merged.push(...array);
    });

    return merged;
  }

  getEntitiesToExportCsv() {
    const groups =  this.sortedRecordsList.map(group => group.transactions.map(x => {
        const localTime = new Date(x.transactionDate).toString();
        const amendColEmpty = x?.activityType === ActivityTypesEnum.Amendment && !x.infoSecondary2;
        return {
          date: localTime.split("GMT")[0].trim(),
          User: x.lastName + ", " + x.firstName,
          drug: x['friendlyName'],
          ndc: x['packageNdc'],
          activity: x.activityType,
          location: x?.locationName,
          starting: x.startingQuantity,
          count: x.addedQuantity,
          onHand: x.endingQuantity,
          info1: amendColEmpty ? '' : x.infoHeading,
          info2: x.infoSecondary,
          info3: amendColEmpty ? x.infoHeading : x?.infoSecondary2
          }})
      );

    const merged = [];
    groups.forEach(array => {
      merged.push(...array);
    });

    return merged;
  }


  handleDownload(res: Blob, fileName: string): void {
    const downloadedFile = new Blob([res], {type: res.type});
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.download = fileName;
    a.href = URL.createObjectURL(downloadedFile);
    a.target = '_blank';
    a.click();
    document.body.removeChild(a);
  }

  changePeriod(period): void {
    if (period && period.id === SearchDateRange.Custom) {
      setTimeout(() => this.customPeriodDatepicker.datepickerInstance?.open());
    } else {
      this.resetCustomRange();
    }
  }

  areFiltersEmpty(): boolean {
    const formValue = this.customReportControlsForm.getRawValue();
    return Object.values(formValue).every((x: any) => !x || (Array.isArray(x) && !x.length));
  }

  customPeriodChanged(date): void {
    const custom = this.periodsList.find(x => x.id === SearchDateRange.Custom);
    custom.name =
      `Custom (${this.datePipe.transform(date.value[0], 'M/d/yyyy')} - ${this.datePipe.transform(date.value[1], 'M/d/yyyy')})`;
    this.periodDropdown.chooseOption(custom);
    (custom as any).value = {startDate: date.value[0], endDate: date.value[1]};
  }

  checkDateRangeAfterClosed(): void {
    const custom = this.periodsList.find(x => x.id === SearchDateRange.Custom);
    if (custom.name === 'Custom Range') {
      this.periodDropdown.chooseOption(this.periodsList.find(x => x.id === SearchDateRange.Custom));
    }
  }

  multiselectOpened(limitName: string, dropdownShowName: string): void {
    setTimeout(() => {
      this.userItemsShowLimit = 1;
      this.drugItemsShowLimit = 1;
      this.activitiesItemsShowLimit = 1;

      this.isDrugsDropdownOpened = false;
      this.isUsersDropdownOpened = false;
      this.isActivityDropdownOpened = false;

      this[limitName] = 4;
      this[dropdownShowName] = true;
    });
  }

  multiselectClosed(limitName: string, dropdownShowName: string): void {
    this.userItemsShowLimit = 2;
    this.drugItemsShowLimit = 2;
    this.activitiesItemsShowLimit = 2;
    this[dropdownShowName] = false;
  }

  switchFilterResultBy(filterBy: 'user' | 'drug'): void {
    this.filterResultsBy = filterBy;
    this.updateSortedList(this.originalRecordsList);
    this.filterBySearchValue(this.reportsTableControlsForm.get('searchValue').value);
  }

  sortByColumn(columnName: string): void {
    this.store.dispatch(new SetCustomReportRecords(this.sort(this.originalRecordsList, columnName)));
  }

  openWidgetForRecord(flatRecord: any): void {
    this.sideWidgetService.openSideWidget(flatRecord.id);
  }

  filterBySearchValue(searchString: string = ''): void {
    searchString = searchString?.replace(/-/g, '');
    const lowerCaseSearchStr = searchString?.toLocaleLowerCase();
    const filterFunction = (search, data) => searchString ?
      data.filter(el => 
        el.rxNumber?.toLowerCase().includes(lowerCaseSearchStr) ||
        el.patientName?.toString().toLowerCase().includes(lowerCaseSearchStr) ||
        el.friendlyName.toLowerCase().toString().includes(lowerCaseSearchStr) ||
        el.packageNdc.replace(/-/g, '').includes(search)) : data;
    const filtered = filterFunction(searchString, [...this.originalRecordsList]);
    this.updateSortedList(filtered);
  }

  openAuditOrOrder(flatTransaction): void {
    this.saveScrollPosition(this.tbody?.nativeElement?.scrollTop);
    const currentTransaction = this.originalRecordsList.find(el => el.id === flatTransaction.id);
    if (currentTransaction?.auditId) {
      event.stopImmediatePropagation();
      this.router.navigate(['audits/details', currentTransaction.auditId,], {state : {
        navigatedFrom: this.router.url
      }
      });
    } else if (currentTransaction?.orderId) {
      event.stopImmediatePropagation();
      this.ordersService.navigateToOrderDetails(this.router, currentTransaction?.orderType, currentTransaction.orderId);
    } else if (currentTransaction?.activityType === ActivityTypesEnum.Compounded) {
      this.router.navigate(['inventory/compounds', currentTransaction.id], {state : {
        navigatedFrom: this.router.url 
      }});
    }
  }

  private saveScrollPosition(scrollTop: number) {
    this.customReportControlsForm.get('scrollTop').setValue(scrollTop);
    this.store.dispatch(new SaveCustomReportFormState(this.customReportControlsForm.getRawValue()));
  }

  private populateDrugFilter(drugs: IDrug[], schedules: number[]): void {
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      schedules = !schedules?.length ? this.drugService.getSchedulesList() : schedules;
    }
    let filteredBySchedule = [];
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      filteredBySchedule = drugs?.filter(x => schedules?.includes(x.schedule)) || [];
    } else filteredBySchedule = drugs || [];

    this.drugsList = filteredBySchedule.map(el => ({id: el.id, name: el.friendlyName, itemName: el.friendlyName, code: el.packageNdc, schedule: el.schedule}));
    this.drugsList = sortByProperty(this.drugsList, 'name', true);
  }

  trackByFn(index, item): number | string {
    return item.id;
  }

  now(): Date {
    return new Date();
  }

  resetCustomRange(): void {
   const customRange = this.periodsList.find(x => x.id === SearchDateRange.Custom);
   customRange.name = 'Custom Range';
   (customRange as any).value = null;
   this.customPeriodDatepicker.reset()
  }
}
